import { Autoplay } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  ArrowBack,
  Download,
  Edit,
  DeleteOutlineRounded,
  Share,
  AddCircleOutlineOutlined,
} from "@mui/icons-material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { isEmpty, map, groupBy, filter } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { listFlyers } from "../../services/flyers";
import { listDetailsArt } from "../../services/studioFolder";
import { useStyles } from "./styles";
import { ArtImage } from "../../components/ArtImage";
import { price } from "../../utils/format";
import { MenuButton } from "../../components/MenuButton";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/system/Unstable_Grid";
import Chip from "@mui/material/Chip";
import { useDialogConfirmation } from "../../hooks/useDialogConfirmation";
import { listVideos } from "../../services/videos";
import { ArtVideo } from "../../components/ArtVideo";
import { DialogEditArt } from "./DialogEditArt";
import { Checkbox } from "../../components/Checkbox";

//assets
import DsLogo from "../../assets/studio/ds.png";
import CanvaLogo from "../../assets/studio/canva.png";

type RenderItemNewArt = {
  title: string;
  subtitle: string;
  icon: string;
  disabled?: boolean;
  errorText?: string;
  error?: boolean;
  onClick?: () => void;
};

export function StudioFolder() {
  const dialog = useDialogConfirmation();
  const params = useParams();
  const location = useLocation();
  const styles = useStyles();
  const navigate = useNavigate();

  const [dialogEditIsOpen, setDialogEditIsOpen] = useState<boolean>(false);

  const art = useQuery(
    ["art", params?.id],
    () => listDetailsArt(Number(params?.id)),
    { enabled: Boolean(params) }
  );

  const flyers = useQuery(
    ["flyers", params?.id],
    () => listFlyers(Number(params?.id)),
    { enabled: Boolean(params) }
  );

  const videos = useQuery(
    ["videos", params?.id],
    () => listVideos(Number(params?.id)),
    { enabled: Boolean(params) }
  );

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleNewArt = useCallback(() => {
    navigate("new-art");
  }, [navigate]);

  const handleNewDistribution = useCallback(() => {
    navigate("new-distribution");
  }, [navigate]);

  const handleNewArtManual = useCallback(() => {
    dialog.show("Arte criada!", "Deseja abrir a nova arte!", {
      onConfirmate: () => {
        console.log("Abriu");
      },
    });
  }, [dialog]);

  const handleDeleteArt = useCallback(() => {
    dialog.show(
      "Deletar arte",
      "Deseja realmente desativar a(s) arte(s) selecionada(s)?",
      {
        onConfirmate: () => {
          console.log("Deletar");
          dialog.close();
        },
      }
    );
  }, [dialog]);

  const handleDialogEdit = useCallback(() => {
    setDialogEditIsOpen((prevState) => !prevState);
  }, []);

  const artName: string = useMemo(() => {
    //@ts-ignore
    return location?.state?.name as string;
  }, [location.state]);

  const flyersData = useMemo(() => {
    const groupData = groupBy(flyers.data, ({ type }) => type);
    const videosStories: any[] = map(
      filter(videos.data, (video) => video.tipo === 3),
      (video) => ({
        ...video,
        video: true,
        name: video.nome_produto,
        image_url: video.link,
      })
    );
    const videosPosts: any[] = map(
      filter(videos.data, (video) => video.tipo === 1),
      (video) => ({
        ...video,
        video: true,
        name: video.nome_produto,
        image_url: video.link,
      })
    );

    if (!isEmpty(videosPosts)) {
      Object.assign(groupData, {
        Post: videosPosts.concat(groupData?.Post || []),
      });
    }
    if (!isEmpty(videosStories)) {
      Object.assign(groupData, {
        Storie: videosStories.concat(groupData?.Storie || []),
      });
    }

    return Object.entries(groupData);
  }, [flyers.data, videos.data]);

  const RenderItemNewArt = ({
    title,
    subtitle,
    icon,
    disabled,
    errorText,
    error,
    onClick,
  }: RenderItemNewArt) => {
    return (
      <MenuItem onClick={onClick} disabled={disabled}>
        <Stack width="100%" spacing={2} direction="row">
          <img className={styles.imgIconNewArt} src={icon} alt="icon-new-art" />
          <Stack spacing={0}>
            <Typography fontWeight={500}>{title}</Typography>
            <Typography noWrap variant="body2">
              {subtitle}
            </Typography>
            {error && <Chip label={errorText} color="error" size="small" />}
          </Stack>
        </Stack>
      </MenuItem>
    );
  };

  const RenderOverlay = () => {
    return (
      <Stack direction="row">
        <Checkbox color="primary" size="medium" />
      </Stack>
    );
  };

  return (
    <>
      <Stack spacing={4}>
        <Stack direction="row" className={styles.root} pb={2}>
          <Stack spacing={2} direction="row" alignItems="center">
            <Button
              onClick={handleGoBack}
              size="small"
              color="primary"
              startIcon={<ArrowBack />}
            >
              Voltar
            </Button>
            <Typography variant="h6">{artName}</Typography>
            <IconButton onClick={handleDialogEdit}>
              <Edit />
            </IconButton>
          </Stack>
          <MenuButton
            showArrow={false}
            startIcon={<AddCircleOutlineOutlined />}
            size="small"
            color="primary"
            variant="contained"
            label="Criar nova arte"
          >
            <RenderItemNewArt
              onClick={handleNewArt}
              error={isEmpty(art.data?.produtos)}
              errorText="Adicione produtos para continuar"
              disabled={isEmpty(art.data?.produtos)}
              title="Estúdio datasales"
              subtitle="Crie artes com a sua lista de produtos"
              icon={DsLogo}
            />
            <RenderItemNewArt
              onClick={handleNewArtManual}
              title="Manual"
              subtitle="Use o estúdio datasales para criar do zero"
              icon={DsLogo}
            />
            <RenderItemNewArt
              title="Canva"
              subtitle="Crie artes no estúdio do Canva"
              icon={CanvaLogo}
            />
          </MenuButton>
        </Stack>
        <Stack spacing={1}>
          <Stack spacing={1} direction="row" alignItems="center">
            <Typography variant="h6">Lista de produtos</Typography>
            <MenuButton
              id="btn-add-products"
              variant="text"
              label="Adicionar produtos"
            >
              <MenuItem>Adicionar produto único</MenuItem>
              <MenuItem>Adicionar por texto</MenuItem>
              <MenuItem>Importar arquivo</MenuItem>
              <MenuItem>Adicionar produtos de outra propaganda</MenuItem>
              <MenuItem>Ver lista de produtos</MenuItem>
            </MenuButton>
          </Stack>
          {art.isLoading ? (
            <Stack direction="row" justifyContent="center">
              <CircularProgress />
            </Stack>
          ) : isEmpty(art.data?.produtos) ? (
            <>
              <Typography color="GrayText" variant="body2">
                Sua lista de produtos está vazia.
              </Typography>
            </>
          ) : (
            <>
              <Swiper
                autoplay={{
                  delay: 2500,
                  disableOnInteraction: false,
                }}
                loop
                slidesPerView={5}
                spaceBetween={30}
                modules={[Autoplay]}
              >
                {map(art.data?.produtos, (produto) => (
                  <SwiperSlide key={produto.id_tabloide_produto.toString()}>
                    <ArtImage
                      image={produto.imagem_produto}
                      name={`${produto.nome_produto} - ${price(
                        produto.preco_dois
                      )}`}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </>
          )}
        </Stack>
        {flyers.isLoading ? (
          <Stack direction="row" justifyContent="center">
            <CircularProgress />
          </Stack>
        ) : isEmpty(flyers.data) ? (
          <Typography color="GrayText" variant="body2">
            Sua lista de artes está vazia.
          </Typography>
        ) : (
          <Stack spacing={4} pb={4}>
            {map(flyersData, (flyer, index) => (
              <Stack key={index.toString()} spacing={2}>
                <Stack spacing={1} direction="row" alignItems="center">
                  <Typography variant="h6">{flyer[0]}</Typography>
                  <Button size="small" color="primary" startIcon={<Download />}>
                    Baixar todos
                  </Button>
                </Stack>

                <Grid container spacing={2}>
                  {map(flyer[1], (produto) => (
                    <Grid xs={2} key={produto.id.toString()}>
                      {produto?.video ? (
                        <ArtVideo
                          video={produto.image_url}
                          name={produto.name}
                        />
                      ) : (
                        <ArtImage
                          overlayComponent={<RenderOverlay />}
                          image={produto.image_url}
                          name={produto.name}
                        />
                      )}
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            ))}
          </Stack>
        )}
        <Stack className={styles.rootFooter} direction="row">
          <Typography variant="caption">
            Selecione imagens para distribuir.
            <br />
            Para distribuir em carrossel, selecione múltiplas imagens.
          </Typography>
          <div
            style={{
              width: "45%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Stack spacing={2} direction="row">
              <Button startIcon={<Download />}>Baixar</Button>
              <Button onClick={handleNewDistribution} startIcon={<Share />}>
                Distribuir
              </Button>
              <Button
                onClick={handleDeleteArt}
                startIcon={<DeleteOutlineRounded />}
              >
                Apagar
              </Button>
            </Stack>
          </div>
        </Stack>
      </Stack>
      <DialogEditArt open={dialogEditIsOpen} onClose={handleDialogEdit} />
    </>
  );
}
